import * as THREE from 'three'

import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, signal } from '@angular/core'

import { applyPresetOp, isIdentityRotation, RotationOp } from '../../../utils/rotation-offsets'

/**
 * Small button toolbar that lets a user nudge a 3D rotation by clicking
 * named preset operations (swap Y/Z, flip vertical, flip horizontal,
 * rotate 90° left/right, reset). Emits the composed matrix on every click;
 * caller decides what to do with it (persist directly, hold for save, etc.).
 *
 * Used by:
 *   - The Edit Scene properties sidebar (scene-level offset)
 *   - The in-viewpoint Edit Orientation overlay (per-viewpoint offset)
 */
@Component({
    selector: 'shared-rotation-offset-toolbar',
    standalone: true,
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './rotation-offset-toolbar.component.html',
    styleUrls: ['./rotation-offset-toolbar.component.scss'],
})
export class RotationOffsetToolbarComponent implements OnChanges {
    @Input() initial: THREE.Matrix4 = new THREE.Matrix4()
    /** 'secondary' on light backgrounds (scene sidebar), 'light' on dark glass panels (in-VT overlay). */
    @Input() variant: 'secondary' | 'light' = 'secondary'
    @Output() matrixChange = new EventEmitter<THREE.Matrix4>()

    get btnClass(): string { return `btn btn-sm btn-outline-${this.variant} text-nowrap` }

    readonly buttons: { op: RotationOp, label: string, title: string, icon: string }[] = [
        { op: 'swapYZ',         label: 'Swap Y/Z',         title: 'Swap Y and Z axes (R_x 90°)',                  icon: 'fa-solid fa-arrows-up-down-left-right' },
        { op: 'flipVertical',   label: 'Flip vertical',    title: 'Flip vertically (R_x 180°)',                   icon: 'fa-solid fa-arrows-up-down' },
        { op: 'flipHorizontal', label: 'Flip horizontal',  title: 'Flip horizontally (R_z 180°)',                 icon: 'fa-solid fa-arrows-left-right' },
        { op: 'rotate90Left',   label: 'Rotate 90° left',  title: 'Rotate 90° counter-clockwise around vertical', icon: 'fa-solid fa-rotate-left' },
        { op: 'rotate90Right',  label: 'Rotate 90° right', title: 'Rotate 90° clockwise around vertical',         icon: 'fa-solid fa-rotate-right' },
        { op: 'reset',          label: 'Reset',            title: 'Clear rotation offset',                        icon: 'fa-solid fa-arrow-rotate-left' },
    ]

    readonly draft = signal<THREE.Matrix4>(new THREE.Matrix4())

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['initial']) {
            this.draft.set(this.initial.clone())
        }
    }

    onClick(op: RotationOp): void {
        const next = applyPresetOp(this.draft(), op)
        this.draft.set(next)
        this.matrixChange.emit(next)
    }

    isIdentity(): boolean {
        return isIdentityRotation(this.draft())
    }
}
